import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Link } from 'react-router';
import ModeCommentIcon from '@mui/icons-material/ModeComment';
import { Checkbox, Divider, FormControlLabel, MenuItem, Select, Switch } from '@mui/material/';
import Button from '@/components/UI/Button/Button';
import ConfirmDialog from '@/components/UI/Dialog/ConfirmDialog';
import authUtils from '@/helpers/authUtils';
import {
  URLDashboard, LANGUAGES, SUPPORT_CHANNEL, VERSION_DESCRIPTION, STORAGE, errorTypes, APPROVER, CREATOR, CREATOR_EXTERNAL,
  ROUTES, FIELD_PARAMS, pathWebSocketConfigParams, isDev, isLocal, getAbsoluteRoute, CAMPAIGN_MANAGER_FILES, isInt, APPROVER_JV, CREATOR_JV,
} from '@/helpers/constants';
import { formatFullRole, getBrowserLanguageAsDefault, getBrowserUtcAsDefault, stopPropagation } from '@/helpers/utils';
import useApiRequest from '@/hooks/useApiRequest';
import useAuth from '@/hooks/useAuth';
import useGlobalSpinnerContext from '@/hooks/useGlobalSpinner';
import useGoTo from '@/hooks/useGoTo';
import useSave from '@/hooks/useSave';
import useSocketContext from '@/hooks/useSocketContext';
import useToastContext from '@/hooks/useToastContext';
import { setOverviewFilters } from '@/redux/OverviewFilters/overviewFilters.action';
import { getFiltersConfig } from '@/redux/OverviewFilters/overviewFilters.reducer';
import { setParamConfiguration } from '@/redux/ParamsConfig/paramsConfig.action';
import { setSettingsInfo, setStandInFirstTimeResalted, setUserAll, setUserConfig, setUserStandIns } from '@/redux/User/user.action';
import { getSettingsInfo, getUserAll, getStandins } from '@/redux/User/user.reducer';
import administrationServices from '@/services/administrationServices';
import usersServices from '@/services/usersServices';
import { FiltersConfig } from '@/types/Filters';
import { UserCompleteInfo, UserConfig, UserSettings, UserStandinComplete } from '@/types/User';
import TIMEZONES from './UserSettingsMenu.utils';
import ChatBotAI from '../ChatBotAI/ChatBotAI';
import DialogIntroSequence from '../DialogIntroSequence/DialogIntroSequence';
import { IcoButtonSimple } from '../UI/IcoButton/IcoButton';
import TooltipComponent from '../UI/Tooltip/TooltipComponent';
import packageInfo from '../../../package';
import styles from './UserSettingsMenu.module.scss';

enum ActionTypeNotification {
  BELL = 'bell',
  EMAIL = 'email',
  RESET = 'reset',
}

type UserSettingsMenuProps = {
  userInfo: UserCompleteInfo;
  settingsInfo: UserSettings;
  filtersConfig: FiltersConfig;
  userStandIns: UserStandinComplete[];
  setUserInfo: (userInfo: UserCompleteInfo) => void;
  setConfig: (config: UserConfig) => void;
  setGlobalFilters: (filters: FiltersConfig) => void;
  setSettings: (settings: UserSettings) => void;
  setStandInResalted: (resalted: boolean) => void;
  setStandIns: (standIns: UserStandinComplete) => void;
  isFiles: boolean;
  dark: boolean;
  setParamConfig: (paramKey: string, paramValue: any) => void;
};

const OPTIONS_LABELS = {
  STAND_IN: 'standIn',
  TIMEZONE: 'timezone',
  LANGUAGE: 'language',
  ROLE: 'role',
  HELP: 'help',
  ABOUT: 'about',
  CART_INTELLIGENCE: 'cartIntelligence',
};

const isEnvWithAI = isDev || isInt || isLocal;

const UserSettingsMenu: React.FC<UserSettingsMenuProps> = (props) => {
  const { userInfo, filtersConfig, setUserInfo, setConfig, setGlobalFilters, settingsInfo, setSettings, isFiles,
    setStandInResalted, dark, userStandIns, setStandIns, setParamConfig } = props;
  const { setLoading } = useGlobalSpinnerContext();
  const { t, i18n } = useTranslation();
  const authService = useAuth();
  const { goTo } = useGoTo();
  const languageSelected = i18n.language.toLowerCase();
  const [state, setState] = useState({
    optionSelected: '',
    manualOpenIntro: false,
    standInWelcomeOpen: false,
    checkDoNotShowAgainStandIn: false,
    isSelected: false,
  });
  const [openClass, setOpenClass] = useState('');
  const { lastMessage } = useSocketContext();
  const showShowIntroSection = authService.checkRoles([CREATOR, CREATOR_EXTERNAL, APPROVER, APPROVER_JV, CREATOR_JV]);

  const { addToast } = useToastContext();
  const menuRef = useRef<HTMLDivElement>(null);

  const saveChanges = useSave(localStorage.getItem(STORAGE.PREVIOUS_TAB));

  const isItemSelectedOpen = state.isSelected ? styles.SelectedOpen : '';

  // Handle click outside
  useEffect(() => {
    if (!openClass) return undefined;

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpenClass('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openClass]);

  const toggleMenu = () => {
    const newClass = openClass === '' ? styles.MenuOpen : '';
    setOpenClass(newClass);
  };

  const itemSelected = (viewSelected, selected) => {
    if (viewSelected !== OPTIONS_LABELS.STAND_IN) {
      setState({
        ...state,
        optionSelected: viewSelected,
        isSelected: selected,
      });
    } else {
      toggleMenu();
    }
  };

  const onSetLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  const changeLanguageName = (data) => LANGUAGES.find((lang) => lang.code === data)?.name;

  const saveUsersConfig = (newConfig) => {
    const userConfig = { ...userInfo.configuration, ...filtersConfig, ...newConfig };
    usersServices
      .saveUsersConfig(authService.userCode, userConfig)
      .catch((error) => addToast(error.message, 'ERROR'));
  };

  const handleOpenStandInPopup = () => {
    setState({ ...state, standInWelcomeOpen: true });
    setStandInResalted(true);
  };

  const saveUsersSettings = async (newSettings) => {
    if (!authService.canAccess()) {
      addToast(`${t('administration.standins.noRolesUser')} Alice`, 'Warning');

      return {};
    }
    const userSettings = { ...settingsInfo, ...newSettings };
    localStorage.setItem(STORAGE.LAST_UTC, settingsInfo.timezone || getBrowserUtcAsDefault());
    try {
      await usersServices
        .saveUsersSettings(authService.userCode, { ...userSettings, language: userSettings?.language?.toUpperCase() });
      const settings = await usersServices.getUsersSettings(authService.userCode);

      return settings;
    } catch (err: any) {
      setSettings({});
      addToast(err.message, 'ERROR');

      return {};
    }
  };

  const changeLanguage = (language) => {
    onSetLanguage(language);
    saveUsersSettings({ language });
    setSettings({ ...settingsInfo, language: language?.toLowerCase() });
  };

  const changeRole = async (role: string) => {
    setLoading(true);
    try {
      const backRole = authUtils.normalizeBackRoleForPersistence(authUtils.oauthRoleToBackRole(role));
      const oldRole = settingsInfo.roleSelected;
      const newSettings = await saveUsersSettings({ roleSelected: backRole });
      await authService.renewToken();

      setSettings(newSettings);
      setUserInfo({
        ...userInfo,
        settings: newSettings,
      });
      authService.setRole(role, false);

      if (oldRole !== backRole) {
        const fileManagerRole = CAMPAIGN_MANAGER_FILES.toUpperCase();

        if (backRole === fileManagerRole) {
          goTo(ROUTES.FILES_LIST);

          return;
        }

        goTo(ROUTES.DASHBOARD, 50);

        window.dispatchEvent(new CustomEvent('roleChanged', {
          detail: {
            oldRole,
            newRole: backRole,
            oauthRole: role,
            settings: newSettings,
          },
        }));
      }
    } catch (err: any) {
      addToast(err.message, 'ERROR');
      console.log('--- renewCaRTT ERROR! ', err);
    } finally {
      setLoading(false);
    }
  };

  const itemSettingsUTC = (timezone) => {
    saveUsersSettings({ timezone });
    setSettings({ ...settingsInfo, timezone });
  };

  const changeCaRTAISetting = async () => {
    setSettings({ ...settingsInfo, cartIntelligence: !settingsInfo.cartIntelligence });
    await saveUsersSettings({ cartIntelligence: !settingsInfo?.cartIntelligence });
  };

  const saveDefaultSettings = (settings) => {
    const languageEmpty = settings.language === '';
    const timezoneEmpty = settings.timezone === '';

    if (languageEmpty || timezoneEmpty) {
      settings.language = settings.language.toLowerCase() || getBrowserLanguageAsDefault();
      settings.timezone = settings.timezone || getBrowserUtcAsDefault();
      usersServices
        .saveUsersSettings(authService.userCode, settings)
        .catch((error) => addToast(error.message, 'ERROR'));
    } else {
      settings.language = settings.language.toLowerCase();
    }
  };

  const handleCloseStandInPopup = () => {
    setState({ ...state, standInWelcomeOpen: false });

    saveUsersConfig({ readIntro: true, ...(state.checkDoNotShowAgainStandIn ? { readStandIn: true } : {}) });

    goTo(ROUTES.STAND_IN);
  };

  const exitIntro = () => {
    if (state.manualOpenIntro) {
      setState({ ...state, manualOpenIntro: false });
    } else if (!userInfo.configuration?.readStandIn && userStandIns?.length === 0) {
      handleOpenStandInPopup();
    }
    setConfig({ ...userInfo.configuration, readIntro: true });
  };

  const userSettingsMenuOptions = [
    {
      label: OPTIONS_LABELS.STAND_IN,
      icon: 'di icon-person-gruppe',
      content: userStandIns?.[0]?.userInfo || 'no stand-in',
    },
    {
      label: OPTIONS_LABELS.TIMEZONE,
      icon: 'di icon-weltkugel-global',
      content: settingsInfo.timezone,
    },
    {
      label: OPTIONS_LABELS.LANGUAGE,
      icon: 'di icon-weltkugel',
      content: changeLanguageName(settingsInfo?.language?.toLowerCase()),
    },
    {
      label: OPTIONS_LABELS.ROLE,
      icon: 'di icon-person-doppelpfeil-austausch',
      content: authService.getRoleSelected(true),
    },
    {
      label: OPTIONS_LABELS.HELP,
      icon: 'di icon-hilfe',
    },
    {
      label: OPTIONS_LABELS.ABOUT,
      icon: 'di icon-information',
    },
  ];

  const experimentalFeaturesMenuOptions = [
    {
      label: OPTIONS_LABELS.CART_INTELLIGENCE,
      icon: 'di icon-reagenzglas-test-bearbeiten',
      content: `CaRT Intelligence ${settingsInfo?.cartIntelligence ? '✅' : '❌'}`,
    },
  ];

  const menuOptions = isFiles
    ? userSettingsMenuOptions.filter(({ label }) => label !== OPTIONS_LABELS.STAND_IN && label !== OPTIONS_LABELS.TIMEZONE)
    : isEnvWithAI
      ? [...userSettingsMenuOptions, ...experimentalFeaturesMenuOptions]
      : userSettingsMenuOptions;

  const menuItemsHeight = menuOptions?.length * 68;

  const userSettingsMenuGenerator = menuOptions.map((option) => {
    const displayedOption = (
      <li key={option.label} onClick={() => itemSelected(option.label, true)} className={`${option.label === OPTIONS_LABELS.CART_INTELLIGENCE ? styles.MenuItemAI : ''}`}>
        <div className={`${styles.MenuItemLeft}`}>
          <i className={`${option.icon} ${styles.MenuIconLeft}`} />
          <div>
            <span className={styles.MenuTitle}>
              {t(`settingsMenu.${option.label}`)}
            </span>
            {(option.content && option.label === OPTIONS_LABELS.STAND_IN)
              && (
                <>
                  <span className={styles.MenuContent}>{option.content}</span>
                  {userStandIns?.length > 1 && (
                    <span className={styles.StandInCounter}>{t('settingsMenu.more', { x: userStandIns.length - 1 })}</span>
                  )}
                </>
              )}
            {option.content && option.label !== OPTIONS_LABELS.STAND_IN && <span className={styles.MenuContent}>{option.content}</span>}
          </div>
        </div>
        {option.label !== OPTIONS_LABELS.STAND_IN && <i className={`di icon-pfeil-chevron-rechts ${styles.MenuIconRight}`} />}
      </li>
    );

    return option.label === OPTIONS_LABELS.STAND_IN
      ? (
        <Link to={getAbsoluteRoute(ROUTES.STAND_IN)} key={option.label}>
          {displayedOption}
        </Link>
      )
      : (
        displayedOption
      );
  });

  const handleLogout = async () => {
    if (['/campaign/', '/sub-campaign/'].find((path) => window.location.pathname.toLowerCase().includes(path))) {
      await saveChanges();
    }
    await authService.logout();
  };

  const handleManualOpenIntro = () => setState({ ...state, manualOpenIntro: true });
  const toggleStandInCheckBox = () => setState({ ...state, checkDoNotShowAgainStandIn: !state.checkDoNotShowAgainStandIn });

  const fetchStandins = useApiRequest(usersServices.getStandIns);
  const getParamConfiguration = useApiRequest(administrationServices.getParameterConfiguration);

  useEffect(() => {
    if (!userInfo.user?.firstName && authService.userRoles?.length > 0) {
      Promise.all([
        usersServices.getCurrentUser(false),
        usersServices.getUsersSettings(authService.userCode),
        usersServices.getUsersConfiguration(authService.userCode),
        fetchStandins(authService.userCode),
        getParamConfiguration(FIELD_PARAMS.DOCMASTER_LINK),
      ])
        .then((res) => {
          const [user, settings, config, standIns, paramConfig] = res;
          const userConfig = {
            readIntro: config.readIntro,
            readStandIn: config.readStandIn,
            readTranslationMessage: config.readTranslationMessage,
            sizeSidebarLeft: config.sizeSidebarLeft,
            sizeSidebarRight: config.sizeSidebarRight,
          };
          const filters = {
            filterOverview: config.filterOverview,
            pillFilterOverview: config.pillFilterOverview,
            rowsPerPageOverview: config.rowsPerPageOverview,
            sortOverview: config.sortOverview,
            visibilityColumnsOverview: config.visibilityColumnsOverview,
            orderColumnsOverview: config.orderColumnsOverview,
            widthColumnsOverview: config.widthColumnsOverview,
          };
          settings.level = user.level;
          const resolvedBackRole = authUtils.resolveSelectedBackRole(settings?.roleSelected, user?.roleSelected, user?.rolesList || []);

          if (resolvedBackRole) {
            settings.roleSelected = resolvedBackRole;
          }
          saveDefaultSettings(settings);
          authService.setRole(settings.roleSelected, true);
          setSettings(settings);
          onSetLanguage(settings.language);
          setUserInfo({ user, settings, configuration: { ...userConfig, ...filters } });
          setGlobalFilters(filters);
          setState({ ...state });
          setStandIns(standIns?.standInListSingleResponseByUserId);
          setParamConfig(paramConfig?.id, paramConfig?.value);
        })
        .catch((error) => {
          if (error?.name !== errorTypes.ABORT_ERROR) addToast(error.message, 'ERROR');
        });
    }
  }, [authService.userCode]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (userInfo.configuration?.readStandIn === false
      && userStandIns?.length === 0
      && userInfo.configuration?.readIntro && !isFiles) {
      handleOpenStandInPopup();
    }
  }, [userStandIns]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (lastMessage?.topic === pathWebSocketConfigParams) {
      setParamConfig(lastMessage?.msg?.key, lastMessage?.msg?.value);
    }
  }, [lastMessage]); // eslint-disable-line react-hooks/exhaustive-deps

  const bellsNotiByRole = settingsInfo?.notificationByRole;

  const onToggleNotificationByRole = async (role?: string, type?: ActionTypeNotification, isReset?: boolean) => {
    setLoading(true);
    let newNotificationByRole = { ...bellsNotiByRole };

    if (isReset) {
      newNotificationByRole = Object.keys(bellsNotiByRole || {}).reduce((acc, key) => {
        acc[key] = { email: true, bell: true };

        return acc;
      }, {});
    } else if (role && type) {
      const roleBackFormat = authUtils.oauthRoleToBackRole(role);
      const currentValue = bellsNotiByRole?.[roleBackFormat]?.[type] ?? false;
      const prevRoleObj = bellsNotiByRole?.[roleBackFormat] ?? { email: false, bell: false };
      const newRoleObj = {
        email: type === 'email' ? !currentValue : prevRoleObj.email,
        bell: type === 'bell' ? !currentValue : prevRoleObj.bell,
      };
      newNotificationByRole = {
        ...newNotificationByRole,
        [roleBackFormat]: newRoleObj,
      };
    }

    setSettings({ ...settingsInfo, notificationByRole: newNotificationByRole });
    await saveUsersSettings({ notificationByRole: newNotificationByRole });
    setLoading(false);
  };

  const resetNotificationsByRoleDisabled = !bellsNotiByRole || Object.values(bellsNotiByRole).every((roleNoti) => roleNoti?.bell && roleNoti?.email);

  return (
    <>
      <div
        ref={menuRef}
        className={styles.UserSettings}
        style={{
          '--menu-items-height': `${menuItemsHeight}px`,
        } as React.CSSProperties}
      >
        <div className={`${styles.RolSelector} ${dark ? styles.RoleSelectorDark : ''}`}>
          <span>{t('settingsMenu.role')}:</span>
          {(authService.getUserRoles().length === 1)
            ? (
              <span>{authService.getRoleSelected(true)}</span>
            )
            : (
              <Select
                value={authService.getRoleSelected(false)}
                onChange={(e) => changeRole(e.target.value)}
                variant="standard"
                size="small"
                disableUnderline
                className={`${dark ? styles.RolSelectDark : ''}`}
                inputProps={{
                  className: `${styles.RolSelectInput} ${dark ? styles.RolSelectInputDark : ''}`,
                }}
                classes={{ icon: dark ? styles.RolSelectIconDark : '' }}
              >
                {authService.getUserRoles().map((role) => (
                  <MenuItem key={role} value={role}>
                    {formatFullRole(role)}
                  </MenuItem>
                ))}
              </Select>
            )}
        </div>
        <div className={`${styles.UserMenuButton} round-icon-big`} onClick={toggleMenu}>
          <i className={`${isFiles ? styles.IconPerson : ''} ${dark ? styles.IconPersonDark : ''}  di icon-person round-icon-big m0`} />
          {authService.userCode}
        </div>
        <div className={`${styles.Popup} ${openClass}`}>
          <div className={styles.Settings}>
            <div className={styles.Header}>
              <span className={styles.UserName}>
                {userInfo.user?.lastName}, {userInfo.user?.firstName}, {userInfo.user?.department}
              </span>
              <Button id="buttonUserSettings" handleClick={handleLogout} buttonStyle="Primary" btnText={t('logout')} />
            </div>
            <ul>{userSettingsMenuGenerator}</ul>
          </div>
          <div className={`${styles.Selected} ${isItemSelectedOpen}`}>
            {state.optionSelected === OPTIONS_LABELS.TIMEZONE && (
              <>
                <div className={styles.Header}>
                  <div className={styles.SelectedTitle}>
                    <i className={`di icon-pfeil-chevron-links ${styles.BackIco}`} onClick={() => itemSelected('', false)} />
                    <span className={styles.SettingTitle}>{t('settingsMenu.timezone')}</span>
                  </div>
                </div>
                <ul className={styles.ListaTimeZone}>
                  {TIMEZONES.map((timezone) => (
                    <li
                      key={timezone.code}
                      className={`${styles.ListItem} ${settingsInfo.timezone === timezone.code ? styles.ItemActive : ''}`}
                      onClick={() => itemSettingsUTC(timezone.code)}
                    >
                      <span className={styles.MenuTitle}>{timezone.code}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
            {/* Language View */}
            {state.optionSelected === OPTIONS_LABELS.LANGUAGE && (
              <>
                <div className={styles.Header}>
                  <div className={styles.SelectedTitle}>
                    <i className={`di icon-pfeil-chevron-links ${styles.BackIco}`} onClick={() => itemSelected('', false)} />
                    <span className={styles.SettingTitle}>{t('settingsMenu.language')}</span>
                  </div>
                </div>
                <ul>
                  <li
                    className={`${styles.ListItem} ${settingsInfo?.language?.toLowerCase() === 'en' ? styles.ItemActive : ''}`}
                    onClick={() => changeLanguage('en')}
                  >
                    <span className={styles.MenuTitle}>{t('settingsMenu.english')}</span>
                  </li>
                  <li
                    className={`${styles.ListItem} ${settingsInfo?.language?.toLowerCase() === 'de' ? styles.ItemActive : ''}`}
                    onClick={() => changeLanguage('de')}
                  >
                    <span className={styles.MenuTitle}>{t('settingsMenu.german')}</span>
                  </li>
                </ul>
              </>
            )}
            {state.optionSelected === OPTIONS_LABELS.ROLE && (
              <>
                <div className={styles.Header}>
                  <div className={styles.SelectedTitle}>
                    <i className={`di icon-pfeil-chevron-links ${styles.BackIco}`} onClick={() => itemSelected('', false)} />
                    <span className={styles.SettingTitle}>{t('settingsMenu.role')}</span>
                  </div>
                  <div className={styles.NotificationsHeader}>
                    <IcoButtonSimple
                      id="btn-reset-notifications-by-role"
                      icon="icon-rueckgaengig-undo"
                      tooltipText={t('settingsMenu.resetNotifications') || ''}
                      handleClick={() => onToggleNotificationByRole('', ActionTypeNotification.RESET, true)}
                      disabled={resetNotificationsByRoleDisabled}
                      tooltipDisable={t('settingsMenu.resetNotifications') || ''}
                    />
                    <div className={styles.LabelNotis}>{t('settingsMenu.bellsEmails')}</div>
                  </div>
                </div>
                <ul className={styles.RoleList}>
                  {authService.getUserRoles().map((role) => (
                    <li
                      key={role}
                      className={`${styles.ListItem} ${authService?.getRoleSelected(false) === role ? styles.ItemActive : ''}`}
                      onClick={() => changeRole(role)}
                    >
                      <div className={styles.MenuRoles}>
                        <div className={styles.RoleName}>{formatFullRole(role)}</div>
                        <div className={styles.NotisOptions}>
                          <TooltipComponent title={t(`settingsMenu.${bellsNotiByRole?.[authUtils.oauthRoleToBackRole(role)]?.bell ? 'disable' : 'enable'}BellsNotificationByRole`, { role: formatFullRole(role) }) || ''}>
                            <Checkbox color="primary" checked={bellsNotiByRole?.[authUtils.oauthRoleToBackRole(role)]?.bell ?? false} onClick={stopPropagation} onChange={() => onToggleNotificationByRole(role, ActionTypeNotification.BELL)} />
                          </TooltipComponent>
                          <TooltipComponent title={t(`settingsMenu.${bellsNotiByRole?.[authUtils.oauthRoleToBackRole(role)]?.email ? 'disable' : 'enable'}EmailsNotificationByRole`, { role: formatFullRole(role) }) || ''}>
                            <Checkbox color="primary" checked={bellsNotiByRole?.[authUtils.oauthRoleToBackRole(role)]?.email ?? false} onClick={stopPropagation} onChange={() => onToggleNotificationByRole(role, ActionTypeNotification.EMAIL)} />
                          </TooltipComponent>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </>
            )}
            {/* Help View */}
            {state.optionSelected === OPTIONS_LABELS.HELP && (
              <>
                <div className={styles.Header}>
                  <div className={styles.SelectedTitle}>
                    <i className={`di icon-pfeil-chevron-links ${styles.BackIco}`} onClick={() => itemSelected('', false)} />
                    <span className={styles.SettingTitle}>{t('settingsMenu.help')}</span>
                  </div>
                </div>
                <div className={styles.HelpContent}>
                  {(!dark && showShowIntroSection) && (
                    <>
                      <div className={styles.HelpTitle}>{t('settingsMenu.introTour')}</div>
                      <div className={styles.HelpSubTitle}>
                        <i className={`di icon-praesentation ${styles.HelpIco}`} />
                        <div className={styles.OpenIntro} onClick={handleManualOpenIntro}>
                          {t('settingsMenu.helpShowIntra')}
                        </div>
                      </div>
                      <Divider />
                    </>
                  )}
                  <div className={styles.HelpTitle}>{t('settingsMenu.contactUs')}</div>
                  <div className={styles.HelpSubTitle}>
                    <ModeCommentIcon className={styles.Icon} />
                    <a href={SUPPORT_CHANNEL} target="_blank" rel="noopener noreferrer">
                      {t('settingsMenu.support')}
                    </a>
                  </div>
                  <Divider />
                  <div className={styles.HelpTitle}>{t('settingsMenu.getMoreInformation')}</div>
                  <div className={styles.HelpSubTitle}>
                    <i className={`di icon-datei ${styles.HelpIco}`} />
                    <a href="https://social.intra.corpintra.net/groups/cart/" target="_blank" rel="noopener noreferrer">
                      {t('settingsMenu.home')}
                    </a>
                  </div>
                  <div className={styles.HelpSubTitle}>
                    <i className={`di icon-person-sprechblase-chat ${styles.HelpIco}`} />
                    <a href={URLDashboard[languageSelected]?.faq} target="_blank" rel="noopener noreferrer">
                      {t('settingsMenu.faq')}
                    </a>
                  </div>
                  <div className={styles.HelpSubTitle}>
                    <i className={`di icon-email-bearbeiten ${styles.HelpIco}`} />
                    <a href={URLDashboard[languageSelected]?.learn} target="_blank" rel="noopener noreferrer">
                      {t('academy.training')}
                    </a>
                  </div>
                </div>
              </>
            )}
            {state.optionSelected === OPTIONS_LABELS.ABOUT && (
              <>
                <div className={styles.Header}>
                  <div className={styles.SelectedTitle}>
                    <i className={`di icon-pfeil-chevron-links ${styles.BackIco}`} onClick={() => itemSelected('', false)} />
                    <span className={styles.SettingTitle}>{t('settingsMenu.about')}</span>
                  </div>
                </div>
                <ul>
                  <li className={styles.About}>
                    <span className={styles.AboutLabel}>{t('campaignRequests.version')}</span>
                    :
                    {' '}
                    {`${packageInfo.version} - ${VERSION_DESCRIPTION}`}
                  </li>
                  <li className={styles.About}>
                    <span className={styles.AboutLabel}>{t('campaignRequests.role')}</span>: {authService.getRoleSelected(true)}
                  </li>
                </ul>
              </>
            )}
            {(state.optionSelected === OPTIONS_LABELS.CART_INTELLIGENCE && isEnvWithAI) && (
              <>
                <div className={styles.Header}>
                  <div className={styles.SelectedTitle}>
                    <i className={`di icon-pfeil-chevron-links ${styles.BackIco}`} onClick={() => itemSelected('', false)} />
                    <span className={styles.SettingTitle}>{t('Experimental Features')}</span>
                  </div>
                </div>
                <ul>
                  <li className={styles.CartAISelector}>
                    <span>{t('cartAI.caRTIntelligence')}</span>
                    <Switch
                      checked={!!settingsInfo?.cartIntelligence}
                      onChange={changeCaRTAISetting}
                      name="flashing"
                      color="primary"
                    />
                  </li>
                </ul>
              </>
            )}
          </div>
        </div>
      </div>
      {(!userInfo.configuration?.readIntro || state.manualOpenIntro) && <DialogIntroSequence close={exitIntro} />}

      {(state.standInWelcomeOpen && !isFiles) && (
        <ConfirmDialog title={t('dialog.standInFirstTime.title')} close={handleCloseStandInPopup}>
          <div className={styles.StandInPopupContent}>
            <p>{t('dialog.standInFirstTime.line1')}</p>
            <p>{t('dialog.standInFirstTime.line2')}</p>
            <br />
            <p>{t('dialog.standInFirstTime.line3')}</p>
            <FormControlLabel
              control={<Checkbox color="primary" checked={state.checkDoNotShowAgainStandIn} onChange={toggleStandInCheckBox} />}
              label={t('translations.translationInfoCheck')}
            />
          </div>
        </ConfirmDialog>
      )}

      {(isEnvWithAI && settingsInfo.cartIntelligence && userInfo.user?.userCode) && <ChatBotAI userId={userInfo.user?.userCode} />}
    </>
  );
};

const mapStateToProps = (state) => ({
  userInfo: getUserAll(state),
  settingsInfo: getSettingsInfo(state),
  filtersConfig: getFiltersConfig(state),
  userStandIns: getStandins(state),
});

const actionCreators = {
  setUserInfo: setUserAll,
  setConfig: setUserConfig,
  setGlobalFilters: setOverviewFilters,
  setSettings: setSettingsInfo,
  setStandInResalted: setStandInFirstTimeResalted,
  setStandIns: setUserStandIns,
  setParamConfig: setParamConfiguration,
};

export default connect(mapStateToProps, actionCreators)(UserSettingsMenu);
